/**
 * "Protect media" (uiOptions.protectMedia): take away the browser's ways of saving the photos,
 * videos and PDFs a creator uploaded, for client work that shouldn't be handed out as files.
 *
 * Inside the viewer container, while it is on:
 *  - <video>: no Download item in the player's menu (controlsList="nodownload") and no
 *    picture-in-picture.
 *  - <img>, <video>, <canvas>: no right-click menu ("Save image as…", "Save video as…"), no
 *    dragging the file out, and no long-press save sheet on iOS.
 *  - PDFs are drawn by the viewer's own pdf.js renderer (pages as images, no toolbar) instead of
 *    the browser's PDF viewer, which has download and print buttons (see pdfInViewer).
 *
 * It is a deterrent, the same as embedding the content from a third-party viewer: anything a
 * browser shows can still be screenshotted or pulled from the browser's network tools.
 */

export const PROTECT_MEDIA_CLASS = 'storysplat-protect-media';

const MEDIA_SELECTOR = 'img, video, canvas';

/** True when `el` sits inside a viewer that protects its media. */
export function isMediaProtected(el: Element | null | undefined): boolean {
    return !!el?.closest?.(`.${PROTECT_MEDIA_CLASS}`);
}

/** Remove the save affordances an element carries itself (the player menu, dragging). */
export function protectMediaElement(el: Element): void {
    if (el.tagName === 'VIDEO') {
        const video = el as HTMLVideoElement;
        // Attributes rather than properties: engines without controlsList (Safari) ignore them.
        video.setAttribute('controlslist', 'nodownload noremoteplayback');
        video.setAttribute('disablepictureinpicture', '');
        video.disablePictureInPicture = true;
        video.draggable = false;
    } else if (el.tagName === 'IMG') {
        (el as HTMLImageElement).draggable = false;
    }
}

function protectTree(node: Node): void {
    if (node.nodeType !== 1) return;
    const el = node as Element;
    if (el.matches('video, img')) protectMediaElement(el);
    el.querySelectorAll('video, img').forEach(protectMediaElement);
}

/**
 * Protect everything shown in `container`, including popups, lightboxes, overlays and menu media
 * added later (watched with a MutationObserver). Returns the undo.
 */
export function installMediaProtection(container: HTMLElement): () => void {
    container.classList.add(PROTECT_MEDIA_CLASS);
    protectTree(container);
    const observer = typeof MutationObserver === 'undefined'
        ? null
        : new MutationObserver((records) => {
            for (const record of records) record.addedNodes.forEach(protectTree);
        });
    observer?.observe(container, { childList: true, subtree: true });
    // Capture phase, so a handler inside a popup can't let the menu through first.
    const blockSave = (e: Event) => {
        const target = e.target as Element | null;
        if (target && typeof target.closest === 'function' && target.closest(MEDIA_SELECTOR)) e.preventDefault();
    };
    container.addEventListener('contextmenu', blockSave, true);
    container.addEventListener('dragstart', blockSave, true);
    return () => {
        observer?.disconnect();
        container.removeEventListener('contextmenu', blockSave, true);
        container.removeEventListener('dragstart', blockSave, true);
        container.classList.remove(PROTECT_MEDIA_CLASS);
    };
}

/** CSS for protected viewers: no iOS long-press save sheet, no selecting media. */
export const PROTECT_MEDIA_CSS = `
    .${PROTECT_MEDIA_CLASS} img,
    .${PROTECT_MEDIA_CLASS} video,
    .${PROTECT_MEDIA_CLASS} canvas {
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      user-select: none;
    }
`;
